import { useState, useEffect, useMemo } from "react";
import { fetchUserStatus } from "../../services/chatAPI";

interface UserData {
  AccessToken?: string;
}

interface EventDetails {
  organiserId?: string;
  challengerId?: string;
  generalChatConversationId?: string;
}

interface CheckInStatuses {
  userID?: string;
  status?: "online" | "offline";
}

interface UserStatus {
  userID: string;
  status: "online" | "offline";
}

interface Conversation {
  conversationId: string;
  members: Record<string, "online" | "offline">;
}

interface FetchUserStatusResponse {
  statusCode: number;
  body: {
    conversations: Conversation[];
  };
}

interface OpponentStatusProps {
  userData: UserData | null;
  eventDetails: EventDetails | null;
  checkInStatuses: CheckInStatuses | null;
  userRole: string;
}

const useOpponentStatus = ({ userData, eventDetails, checkInStatuses, userRole }: OpponentStatusProps) => {
  const [userStatus, setUserStatus] = useState<UserStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const opponentId = useMemo(
    () => (userRole === "organiser" ? eventDetails?.challengerId : eventDetails?.organiserId),
    [userRole, eventDetails]
  );

  const fetchUserStatusData = async () => {
    if (!userData?.AccessToken || !eventDetails?.generalChatConversationId) return;

    setIsLoading(true);
    try {
      const response = (await fetchUserStatus(userData.AccessToken)) as FetchUserStatusResponse;
      if (response.statusCode === 200 && eventDetails.generalChatConversationId) {
        const conversation = response.body.conversations.find(
          (conv) => conv.conversationId === eventDetails.generalChatConversationId
        );
        setUserStatus(
          conversation && Object.keys(conversation.members).length > 0
            ? {
                userID: Object.keys(conversation.members)[0],
                status: Object.values(conversation.members)[0],
              }
            : null
        );
      } else {
        setUserStatus(null);
      }
    } catch (error) {
      console.error("Error fetching user status:", error);
      setUserStatus(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUserStatusData();
  }, [userData?.AccessToken, eventDetails?.generalChatConversationId]);

  useEffect(() => {
    if (eventDetails?.generalChatConversationId && checkInStatuses?.userID) {
      const { userID, status } = checkInStatuses;
      setUserStatus((prev) =>
        prev && prev.userID === userID ? { ...prev, status } : { userID, status }
      );
    }
  }, [checkInStatuses, eventDetails?.generalChatConversationId]);

  return {
    opponentId,
    isOpponentOnline: userStatus?.userID === opponentId && userStatus?.status === "online",
    isLoading,
  };
};

export default useOpponentStatus;